import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Play, Trophy, Clock, Ticket, X, Users } from 'lucide-react';
import { Button } from './ui/button';
import SlotMachineRoulette from './SlotMachineRoulette';
import confetti from 'canvas-confetti';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LiveDrawAdminModal = ({ draw, onClose, onComplete }) => {
  const [phase, setPhase] = useState('confirm'); // confirm, countdown, spinning, winner
  const [countdown, setCountdown] = useState(10);
  const [drawData, setDrawData] = useState(null);
  const [winner, setWinner] = useState(null);
  const [isStarting, setIsStarting] = useState(false);
  const [spinComplete, setSpinComplete] = useState(false);

  const token = localStorage.getItem('token');

  // Check if draw is already in live state on mount
  useEffect(() => {
    const checkExistingState = async () => {
      try {
        // Use admin endpoint to get the pre-selected winning ticket
        const response = await axios.get(`${API}/admin/draws/${draw.id}/live-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const status = response.data.status;
        
        if (status === 'countdown' || status === 'spinning') {
          // Resume the live draw
          setDrawData({
            ...draw,
            all_tickets: response.data.all_tickets,
            ticket_count: response.data.ticket_count,
            winning_ticket: response.data.winning_ticket
          });
          
          if (status === 'countdown') {
            // Calculate remaining countdown
            const startTime = new Date(response.data.draw_started_at).getTime();
            const elapsed = Math.floor((Date.now() - startTime) / 1000);
            const remaining = Math.max(0, 10 - elapsed);
            setCountdown(remaining);
            
            if (remaining > 0) {
              setPhase('countdown');
            } else {
              setPhase('spinning');
            }
          } else {
            setPhase('spinning');
          }
        } else if (status === 'completed') {
          // Draw already completed
          setWinner({
            name: response.data.winner_name,
            ticket: response.data.winning_ticket
          });
          setPhase('winner');
        }
      } catch (error) {
        console.error('Error checking draw state:', error);
      }
    };
    
    checkExistingState();
  }, [draw.id, draw]);

  // Start the live draw
  const startLiveDraw = async () => {
    setIsStarting(true);
    try {
      const response = await axios.post(
        `${API}/admin/draws/${draw.id}/start-live-draw`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setDrawData({
        ...draw,
        all_tickets: response.data.all_tickets,
        ticket_count: response.data.ticket_count,
        winning_ticket: response.data.winning_ticket  // Get winning ticket for animation
      });
      
      toast.success('Live draw started! Users are being notified.');
      setPhase('countdown');
    } catch (error) {
      console.error('Error starting live draw:', error);
      toast.error(error.response?.data?.detail || 'Failed to start live draw');
      setIsStarting(false);
    }
  };

  // Countdown timer
  useEffect(() => {
    if (phase === 'countdown' && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            // Notify backend that spinning is starting
            axios.post(
              `${API}/admin/draws/${draw.id}/start-spinning`,
              {},
              { headers: { Authorization: `Bearer ${token}` } }
            ).catch(err => console.error('Error starting spinning:', err));
            
            setPhase('spinning');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [phase, countdown, draw.id, token]);

  // Handle spin complete - finalize the draw
  const handleSpinComplete = useCallback(async () => {
    setSpinComplete(true);
    
    // Complete the draw on the backend
    try {
      const response = await axios.post(
        `${API}/admin/draws/${draw.id}/complete-live-draw`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setWinner({
        name: response.data.winner?.name,
        ticket: response.data.winning_ticket
      });
      
      // Trigger confetti
      confetti({
        particleCount: 150,
        spread: 100,
        origin: { y: 0.6 }
      });
      
      setTimeout(() => setPhase('winner'), 1500);
    } catch (error) {
      console.error('Error completing draw:', error);
      toast.error('Error completing draw');
    }
  }, [draw.id, token]);

  // Poll for draw status to get the winning ticket (admin endpoint)
  useEffect(() => {
    if (phase === 'spinning' || phase === 'countdown') {
      const pollStatus = async () => {
        try {
          const response = await axios.get(`${API}/admin/draws/${draw.id}/live-status`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.data.winning_ticket && !drawData?.winning_ticket) {
            setDrawData(prev => ({
              ...prev,
              winning_ticket: response.data.winning_ticket
            }));
          }
        } catch (err) {
          // Ignore errors
        }
      };
      
      const interval = setInterval(pollStatus, 1000);
      return () => clearInterval(interval);
    }
  }, [phase, draw.id, drawData?.winning_ticket, token]);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/80 p-3 backdrop-blur-sm sm:p-4"
      >
        <motion.div
          initial={{ scale: 0.9, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          className="relative w-full max-w-2xl rounded-3xl border-2 border-[#A855F7] bg-[#18181b] p-5 sm:p-8"
        >
          {/* Close button */}
          {phase === 'confirm' && (
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-[#52525b] hover:text-white"
            >
              <X className="w-6 h-6" />
            </button>
          )}

          {/* Confirm Phase */}
          {phase === 'confirm' && (
            <div className="text-center">
              <div className="inline-flex items-center gap-2 bg-[#A855F7]/20 px-4 py-2 rounded-full mb-6">
                <Play className="w-5 h-5 text-[#A855F7]" />
                <span className="text-[#A855F7] font-bold">LIVE DRAW</span>
              </div>
              
              <h2 className="text-2xl font-heading font-bold text-white mb-2">
                Start Live Draw
              </h2>
              <p className="text-[#a1a1aa] mb-6">
                {draw.title}
              </p>
              
              <div className="bg-[#27272a] rounded-xl p-4 mb-6 text-left">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-[#a1a1aa]">Prize</div>
                    <div className="text-[#EAB308] font-semibold">{draw.prize_description}</div>
                  </div>
                  <div>
                    <div className="text-sm text-[#a1a1aa]">Draw Date</div>
                    <div className="text-white">{new Date(draw.draw_date).toLocaleDateString()}</div>
                  </div>
                </div>
              </div>
              
              <div className="bg-[#EAB308]/10 border border-[#EAB308]/30 rounded-xl p-4 mb-6 text-left">
                <h4 className="text-[#EAB308] font-semibold mb-2 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  What happens next:
                </h4>
                <ul className="text-sm text-[#a1a1aa] space-y-1">
                  <li>• All participants will be notified instantly</li>
                  <li>• A 10-second countdown will begin</li>
                  <li>• The slot machine roulette will spin for 15 seconds</li>
                  <li>• Winner will be announced with celebration!</li>
                </ul>
              </div>
              
              <div className="flex gap-4">
                <Button
                  onClick={onClose}
                  variant="outline"
                  className="flex-1 border-[#27272a] text-[#a1a1aa] hover:text-white"
                >
                  Cancel
                </Button>
                <Button
                  onClick={startLiveDraw}
                  disabled={isStarting}
                  className="flex-1 bg-[#A855F7] hover:bg-[#9333EA] text-white"
                >
                  {isStarting ? (
                    'Starting...'
                  ) : (
                    <>
                      <Play className="w-4 h-4 mr-2" />
                      Start Live Draw
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Countdown Phase */}
          {phase === 'countdown' && (
            <div className="text-center">
              <div className="inline-flex items-center gap-2 bg-[#EAB308]/20 px-4 py-2 rounded-full mb-6">
                <Clock className="w-5 h-5 text-[#EAB308]" />
                <span className="text-[#EAB308] font-bold">COUNTDOWN</span>
              </div>
              
              <h2 className="text-xl font-heading text-white mb-2">
                {draw.title}
              </h2>
              <p className="text-[#a1a1aa] mb-4">
                Users are watching...
              </p>
              
              <motion.div
                key={countdown}
                initial={{ scale: 1.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="text-9xl font-heading font-bold text-[#EAB308] mb-6"
              >
                {countdown}
              </motion.div>
              
              <div className="flex items-center justify-center gap-2 text-[#a1a1aa]">
                <Ticket className="w-5 h-5" />
                <span>{drawData?.ticket_count || 0} tickets in this draw</span>
              </div>
            </div>
          )}

          {/* Spinning Phase */}
          {phase === 'spinning' && (
            <div className="text-center">
              <div className="inline-flex items-center gap-2 bg-[#A855F7]/20 px-4 py-2 rounded-full mb-6">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                >
                  <Ticket className="w-5 h-5 text-[#A855F7]" />
                </motion.div>
                <span className="text-[#A855F7] font-bold">SPINNING...</span>
              </div>
              
              <h2 className="text-xl font-heading text-white mb-6">
                {draw.title}
              </h2>
              
              <div className="flex justify-center mb-6">
                <SlotMachineRoulette
                  tickets={drawData?.all_tickets || []}
                  winningTicket={drawData?.winning_ticket}
                  isSpinning={!spinComplete}
                  onSpinComplete={handleSpinComplete}
                />
              </div>
            </div>
          )}

          {/* Winner Phase */}
          {phase === 'winner' && (
            <div className="text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', bounce: 0.5 }}
              >
                <div className="inline-flex items-center gap-2 bg-[#EAB308]/20 px-4 py-2 rounded-full mb-6">
                  <Trophy className="w-5 h-5 text-[#EAB308]" />
                  <span className="text-[#EAB308] font-bold">WINNER!</span>
                </div>
              </motion.div>
              
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                <h2 className="text-3xl font-heading font-bold text-white mb-2">
                  Congratulations!
                </h2>
                
                <div className="bg-[#27272a] rounded-xl p-6 mb-6">
                  <div className="text-sm text-[#a1a1aa] mb-2">Winner</div>
                  <div className="text-2xl font-bold text-[#EAB308] mb-4">
                    {winner?.name}
                  </div>
                  <div className="text-sm text-[#a1a1aa] mb-1">Winning Ticket</div>
                  <div className="bg-[#EAB308] text-black font-mono text-xl font-bold px-6 py-2 rounded-lg inline-block">
                    #{winner?.ticket}
                  </div>
                </div>
                
                <p className="text-[#a1a1aa] mb-6">
                  Prize: <span className="text-[#EAB308]">{draw.prize_description}</span>
                </p>
                
                <Button
                  onClick={onComplete}
                  className="bg-[#EAB308] hover:bg-[#CA8A04] text-black font-bold px-8"
                >
                  Done
                </Button>
              </motion.div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default LiveDrawAdminModal;
